<template>
  <div class="blog">
    <div class="container background-container">
      <div class="row">
        <div class="col-md-12 p-0 pt-4">
          <img
            class="blog__header-image"
            src="@/assets/images/blog/growyourbrandwithtiktok/header.jpg"
            alt="header-image"
          />
        </div>
      </div>
    </div>

    <div class="container p-md-0">
      <div class="row">
        <div class="col-md-12 p-md-0">
          <h1 class="blog__title">Grow and expand your brand with TikTok</h1>
        </div>
      </div>

      <div class="row position-relative">
        <div
          v-if="userAgent && !isInstagramBrowser"
          class="blog__social d-md-flex d-none"
        >
          <span class="blog__social-title">Share</span>
          <div
            v-for="(link, key) in socialLinks"
            :key="key"
            class="mt-2"
            @click="handleShareClick"
          >
            <img :src="getImageItem(key)" :alt="key" />
          </div>
        </div>
        <div class="col-md-8 offset-md-2">
          <p>
            For anyone really serious about boosting their social media status
            and really engaging with an audience, then there’s one platform you
            need to use – TikTok.
          </p>
          <p>
            In 2020 it was the most downloaded app, with predictions it will
            reach 1.8 BILLION users of the end of 2023. Even the tiniest slice
            of that pie can help creators make a life changing impact.
          </p>
          <p>
            If you don’t know much about it, here’s the lowdown. TikTok focuses
            on short-form videos, which are suggested to users based on its
            unique algorithm.
          </p>
          <p>
            It’s a brilliant way for creators and an audience to engage and if
            you only want to concentrate on one social media platform, this
            should definitely be top of your list. TikTok shows users videos
            which it knows are of interest to them, by analysing what they have
            watched, liked or shared.
          </p>
          <p>
            That means that if you’re posting your recipes or showing your
            cooking – or even sharing your make-up demos or exercise routines –
            the algorithm will help direct them to an audience with those
            particular interests.
          </p>
          <p>
            The app’s main demographic is currently 13-24-year-olds, but there
            is no absolute threshold for engagement - anyone can join, and
            people of all ages are now enjoying what TikTok has to offer.
          </p>
          <p>
            Everyone on TikTok has a “For You” page often referred to as ‘Fyp’
            which offers them a feed of videos, which is tailored to their
            interests. This is different to other social media apps, where your
            main feed is composed of the people that you have already chosen to
            follow.
          </p>
          <p>
            On TikTok, this has meant that certain brands and artists have
            quickly accelerated from obscurity to viral success, just by one
            video making it to many ‘For You’ pages. Another unique part of
            TikTok is how the content is made. The app itself comes equipped
            with everything you need to record, edit, and upload a video. This
            is different to other popular video-sharing platforms such as
            YouTube. The result of this has been more authentic, natural videos
            which are recorded and uploaded quickly, as opposed to edited and
            stylised for a long amount of time.
          </p>
          <p>
            This could also be down to the fast-paced nature of the app, where
            many different videos can go viral every day. TikTok was the fastest
            app in the history of social media to reach a billion user
            downloads, and it continues to make an impact as a fast-paced,
            unique, and popular social media site in 2023.
          </p>
          <p>
            For anyone who has created a cookbook – or is looking to do so in
            the future – with Found, TikTok is the perfect outlet to showcase
            your skills, build your followers – and sell your products!
          </p>

          <h2>Why is it an excellent way to grow your brand?</h2>

          <p>
            The kind of advertising that does well on TikTok is a little bit
            different to other social media sites. Forbes claims that
            “Businesses that thrive the most on TikTok are ones that post videos
            that are fun, light-hearted and visually pleasing, and that follow
            popular trends.” Due to its algorithm, finding what works for your
            business and your followers takes some experimenting on TikTok. It
            can often be jokey, silly videos that take off quicker than slick
            advertisements. However, if you can work out what makes your
            audience engage, and get your posts to the “For You” page, TikTok is
            a uniquely effective way to market your brand.
          </p>
          <p>
            Firstly, you can directly engage with your customers, through the
            ‘duet’ and ‘stitch’ features which allow them to add on to your
            existing videos. Secondly, you can engage the other way around with
            existing videos that already discuss your product. Instead of just
            commenting your thanks as you might on Instagram for a good review,
            you can duet the video to add something new or show your
            appreciation.
          </p>
          <p>
            Additionally, such features can be used to engage and collaborate
            with influencers, or “creators” as they are often called on TikTok.
            These are people who know exactly how to create content which will
            be successful on the algorithm and make it to the “For You” page,
            and their creativity can help to boost your product.
          </p>

          <h2>What does TikTok offer specifically for brands?</h2>

          <p>
            In 2020, TikTok launched “TikTok for Business” - a platform within
            TikTok that is made for brands who are looking to invest in
            advertising and marketing. This can be a great way to capitalise on
            the huge amount (over a billion!) of active users that the app
            boasts. There are many types of ads that the app offers, from
            “TopView” - the first video that will be visible to someone opening
            the app, to “Hashtag Challenges” - an opportunity to promote a
            hashtag that is linked to your business, to putting you in touch
            with relevant creators who will promote your product. You can find
            out the ways that brands have used TikTok for business, as well as
            learn about more of the advertising possibilities here:
            <a href="https://www.tiktok.com/business/" target="_blank">
              www.tiktok.com/business/
            </a>
          </p>

          <h2>How can I create an account?</h2>

          <p>
            Creating an account on TikTok is super easy, especially if your
            brand already has a Google, Twitter or Facebook account.
          </p>

          <p>
            First, go to:
            <a href="https://www.tiktok.com/signup" target="_blank">
              www.tiktok.com/signup
            </a>
          </p>

          <p>
            Next, choose from the options on the page. This is where you can use
            an existing account from another platform to make a TikTok account:
          </p>

          <img
            class="blog__landscape-image"
            src="@/assets/images/blog/growyourbrandwithtiktok/2.jpg"
            alt="image"
          />

          <p>
            If you don’t have any existing social media accounts on the
            platforms listed, click ‘Use phone or email’ and you will be taken
            to a page where you can create an account using one or the other:
          </p>

          <img
            class="blog__landscape-image"
            src="@/assets/images/blog/growyourbrandwithtiktok/3.jpg"
            alt="image"
          />

          <p>
            You should be ready to go on TikTok! Once you have set your account
            up, make sure you are logged in on a mobile device, and they are the
            best for using, creating, and engaging with TikTok.
          </p>

          <h2>In Conclusion</h2>

          <p>
            TikTok is a fast-growing phenomenon of an app which offers a unique
            platform to advertise and share your business. The social media
            platform is ideal if you are aiming your product at a younger
            audience, as their core users belong to a young demographic. The
            kind of content that does well on TikTok is authentic, filmed on
            remote devices and includes entertaining, human content. The power
            of the algorithm means that if your video makes it to enough “For
            You” pages, your business can be viewed by thousands of people, even
            if your following is only in the hundreds. Working out how to
            produce content that makes it onto the coveted “For You” page takes
            some practice, but with the help of TikTok’s business suite, as well
            as engagement with existing customers and collaborations with
            creators, the app offers a fantastic ability to grow and expand your
            brand.
          </p>

          <div
            v-if="userAgent && !isInstagramBrowser"
            class="blog__social d-md-none"
          >
            <span class="blog__social-title">Share</span>
            <div class="d-flex">
              <div
                v-for="(link, key) in socialLinks"
                :key="key"
                class="mt-2 mr-md-0 mr-3"
                @click="handleShareClick"
              >
                <img :src="getImageItem(key)" :alt="key" />
              </div>
            </div>
          </div>

          <div v-if="tags && tags.length">
            <h2 class="mt-4">Related topics</h2>
            <div class="blog__tags">
              <Tag
                v-for="tag in tags"
                :key="tag"
                class="mr-3 my-2"
                @click="onTagClick(tag)"
              >
                {{ tag }}
              </Tag>
            </div>
          </div>

          <div class="blog__action-button mt-5">
            <Button
              class="pt-2"
              full-width
              size="sm"
              @click="handleBeFoundClick"
            >
              I’m ready to talk
            </Button>
          </div>

          <div class="blog__action-button mt-3 mb-5">
            <Button
              class="pt-2"
              full-width
              size="sm"
              variant="outline"
              @click="$router.push('/blog')"
            >
              View all blogs
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { throttle } from 'lodash';
import socialLinks from '@/misc/socialLinks';
import { CarouselModes, WebApiCancelError } from '@/misc/constants';
import Tag from '@/components/Elements/Tag.vue';
import Button from '@/components/Elements/Button.vue';
import getDefaultProductFormat from '@/misc/getDefaultProductFormat';
import formatMoney from '@/misc/formatMoney';
import getFormatPrice from '@/misc/getFormatPrice';

export default {
  name: 'BlogItemPage',
  components: {
    Button,
    Tag
  },

  data() {
    return {
      creatorHandle: '626ad14797861a0010350d6c',
      CarouselModes,
      socialLinks,
      buyNowLoading: false,
      creatorProducts: [],
      tags: ['General']
    };
  },

  head: {
    title: 'Be Found | Blog'
  },

  computed: {
    ...mapState('main', ['userAgent']),
    ...mapGetters({
      redirectUrl: 'checkout/redirectUrl',
      isInstagramBrowser: 'main/isInstagramBrowser',
      currency: 'config/currency',
      region: 'config/region'
    })
  },
  methods: {
    ...mapMutations({
      ADD_ITEM: 'checkout/ADD_ITEM'
    }),
    ...mapActions({
      createSession: 'checkout/createSession'
    }),
    getImageItem(key) {
      return require(`@/assets/icons/social/${key}.svg`);
    },

    onTagClick(tag) {
      this.$router.push('/blog?filter=' + tag);
    },

    handleBeFoundClick() {
      this.$router.push({ path: '/be-found' });
    },

    handleShareClick: throttle(function () {
      const url = window.location.href;
      if (navigator?.share) {
        navigator.share({ url }).catch((err) => {
          if (!err.message.includes(WebApiCancelError)) {
            this.$showToastMessage(err.message, 'Error', 'error');
          }
        });
      } else if (navigator?.clipboard) {
        navigator.clipboard
          .writeText(url)
          .then(() =>
            this.$showToastMessage('Link is copied', 'Success', 'success')
          )
          .catch((err) =>
            this.$showToastMessage(
              `Failed to copied link ${err.message}`,
              'Error',
              'error'
            )
          );
      } else {
        this.$showToastMessage(
          `Sharing function is not supported`,
          'Error',
          'error'
        );
      }
    }, 1500),

    getProductFormatPrice(product) {
      const defaultFormat = getDefaultProductFormat(product);
      return formatMoney(
        getFormatPrice(defaultFormat, this.region)?.amount,
        this.currency
      );
    },

    handleBuyNowClick(product) {
      this.buyNowLoading = true;
      this.ADD_ITEM({
        product,
        count: 1,
        productFormatId: getDefaultProductFormat(product)?._id
      });
      this.createSession()
        .then(() => {
          window.location.href = this.redirectUrl;
        })
        .catch((err) => {
          this.$showToastMessage(
            this.$get(err, 'response.data.message'),
            'Error',
            'danger'
          );
        })
        .finally(() => {
          this.buyNowLoading = false;
        });
    },

    handleProductBundleClick(product) {
      this.$router.push({
        path: `/products/${product.id || product.item.id}`
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.blog {
  &__header-image {
    object-fit: cover;
    height: 480px;
    width: 100%;
  }

  &__title {
    padding-top: 40px;
    text-align: left;
    line-height: 56px;
  }

  &__landscape-image {
    object-fit: cover;
    transform: translate3d(0, 0, 1px);
    height: 550px;
    width: 100%;
    margin-bottom: 40px;
  }

  &__social {
    position: absolute;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    text-align: center;

    &-title {
      font-size: 12px;
    }
  }

  &__double-image {
    display: flex;
    justify-content: space-around;

    img {
      object-fit: cover;
      height: 700px;
      &:first-child {
        margin-right: 20px;
      }
    }
  }

  &__quote {
    font-family: 'Poppins-SemiBold', sans-serif;
    font-size: 24px;
    border-left: 8px solid $primary-color;
    padding-left: 35px;
  }

  &__action-button {
    margin: 0 auto;
    max-width: 240px;
  }

  @media (max-width: $screen-width-md) {
    &__header-image {
      object-fit: contain;
      height: 242px;
    }

    &__landscape-image {
      height: 328px;
    }

    &__double-image {
      flex-wrap: wrap;

      img {
        margin-bottom: 25px;
        object-fit: cover;
        height: 420px;
        &:first-child {
          margin-right: 0;
        }
      }
    }

    &__social {
      position: initial;
      flex-direction: column;
      text-align: left;
    }
  }
}
</style>
